import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, CameraIcon } from 'lucide-react';
interface HeroSectionProps {
  onNavigate: (view: 'investor' | 'foundher') => void;
}
export function HeroSection({
  onNavigate
}: HeroSectionProps) {
  return <main className="relative min-h-screen w-full flex items-center justify-center overflow-hidden" data-id="element-126">
      {/* Background Image */}
      <div className="absolute inset-0 z-0" data-id="element-127">
        <img src="https://cdn.magicpatterns.com/uploads/jzeps71oGdi7nCuoJSWj2o/pexels-rdne-7490837.jpg" alt="Women networking" className="w-full h-full object-cover object-center" data-id="element-128" />
      </div>

      {/* Dark Gradient Overlay */}
      {/* Mobile: Top-to-bottom gradient. Desktop: Left-to-right gradient */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#0a0e27]/95 via-[#0a0e27]/85 to-[#0a0e27]/70 md:bg-gradient-to-r md:from-[#0a0e27]/95 md:via-[#0a0e27]/80 md:to-[#0a0e27]/20" data-id="element-129" />

      {/* Content Container */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-6 py-16 md:py-24 md:pt-48 flex flex-col md:flex-row items-center md:items-end justify-between gap-12 lg:gap-16" data-id="element-130">
        {/* Left Column: Logo & Title */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="flex-1 flex flex-col items-center md:items-start text-center md:text-left w-full pt-8 md:pt-0" data-id="element-131">
          <img src="https://cdn.magicpatterns.com/uploads/sgVCwNAcYebHnTHgGAhSNM/Momentum_-_Edited.png" alt="Momentum Logo" className="w-48 md:w-64 lg:w-80 h-auto mb-6 md:mb-8 drop-shadow-[0_0_30px_rgba(79,195,247,0.4)]" data-id="element-132" />

          <div className="space-y-3 md:space-y-4" data-id="element-133">
            <h1 className="font-sora font-extrabold text-5xl md:text-7xl lg:text-8xl text-white tracking-tight text-glow-white leading-none" data-id="element-134">
              MOMENTUM
            </h1>
            <p className="font-sora font-bold tracking-[0.2em] md:tracking-[0.3em] text-xs md:text-sm lg:text-base text-accent-cyan uppercase" data-id="element-135">
              Energy • Access • Capital
            </p>
          </div>

          <div className="mt-6 md:mt-8 max-w-lg" data-id="element-136">
            <p className="font-inter text-white/95 text-sm md:text-base lg:text-lg leading-relaxed mb-3" data-id="element-137">
              <strong className="text-white font-semibold" data-id="element-138">
                One room. 15+ founders. 15+ investors. And the supporters who
                made it all possible. The time was magnetic.
              </strong>
            </p>
            <p className="font-inter text-white/70 text-sm md:text-base leading-relaxed mb-4" data-id="element-139">
              This was not a networking mixer. This was a high-impact investor
              match experience designed to accelerate ambitious founders into
              real fundraising conversations.
            </p>
            <p className="font-inter text-accent-pink font-medium text-sm md:text-base leading-relaxed" data-id="element-140">
              Thank you to our partners at Protexxa for making this possible.
            </p>
            <a href="https://drive.google.com/drive/folders/1ZSDPYbwTlbKuAEblHS7QRBAqgqUCNGmZ?usp=drive_link" target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-5 px-6 py-3 rounded-full bg-gradient-to-r from-accent-pink to-accent-purple font-sora font-semibold text-sm md:text-base text-white tracking-wide shadow-lg shadow-accent-pink/25 hover:shadow-accent-pink/40 hover:scale-[1.03] transition-all duration-200" data-id="element-141">
              <CameraIcon className="w-4 h-4 mr-2" data-id="element-142" />
              View Event Photos
            </a>
          </div>
        </motion.div>

        {/* Right Column: Glass Panel & Geometric Lines */}
        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="w-full md:w-[50%] lg:w-[45%] relative" data-id="element-143">
          {/* Decorative Geometric Lines (Desktop Only) */}
          <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] z-0 pointer-events-none" data-id="element-144">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible opacity-60" data-id="element-145">
              {/* Outer Hexagon */}
              <polygon points="50,-10 105,20 105,80 50,110 -5,80 -5,20" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" data-id="element-146" />
              {/* Inner Cyan Hexagon */}
              <polygon points="50,-5 100,22 100,78 50,105 0,78 0,22" fill="none" stroke="rgba(79,195,247,0.3)" strokeWidth="1" data-id="element-147" />
              {/* Accent diagonal lines */}
              <line x1="-10" y1="10" x2="20" y2="-10" stroke="rgba(224,64,251,0.4)" strokeWidth="1" data-id="element-148" />
              <line x1="110" y1="90" x2="80" y2="110" stroke="rgba(79,195,247,0.4)" strokeWidth="1" data-id="element-149" />
            </svg>
          </div>

          {/* Glass Content Panel */}
          <div className="glass-card rounded-2xl p-6 md:p-8 lg:p-10 relative overflow-hidden z-10 shadow-2xl bg-[#0a0e27]/40 backdrop-blur-xl border border-white/10" data-id="element-150">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-cyan via-accent-purple to-accent-pink" data-id="element-151" />

            <div className="space-y-6 md:space-y-8" data-id="element-152">
              {/* Date Badge */}
              <div className="inline-flex items-center space-x-2 md:space-x-3 text-xs md:text-sm lg:text-base font-sora font-semibold text-white/90 bg-white/10 border border-white/10 py-2.5 px-5 md:px-6 rounded-full" data-id="element-153">
                <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-accent-pink flex-shrink-0" data-id="element-154" />
                <span data-id="element-155">March 28 • 4 PM – 8 PM • Toronto</span>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full" data-id="element-156">
                <button onClick={() => onNavigate('investor')} className="flex-1 group relative overflow-hidden rounded-xl bg-gradient-to-br from-accent-cyan/30 to-blue-600/20 border border-accent-cyan/40 p-4 md:p-5 transition-all hover:from-accent-cyan/40 hover:to-blue-600/30 glow-cyan" aria-label="Enter Investor Experience" data-id="element-157">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-cyan/10 to-transparent" data-id="element-158" />
                  <div className="relative flex items-center justify-center gap-2" data-id="element-159">
                    <span className="font-sora font-semibold text-white text-sm md:text-base" data-id="element-160">
                      I am an Investor
                    </span>
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-accent-cyan group-hover:translate-x-1 transition-transform" data-id="element-161" />
                  </div>
                </button>

                <button onClick={() => onNavigate('foundher')} className="flex-1 group relative overflow-hidden rounded-xl bg-gradient-to-br from-accent-pink/30 to-purple-600/20 border border-accent-pink/40 p-4 md:p-5 transition-all hover:from-accent-pink/40 hover:to-purple-600/30 glow-pink" aria-label="Enter FoundHer Experience" data-id="element-162">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent-pink/10 to-transparent" data-id="element-163" />
                  <div className="relative flex items-center justify-center gap-2" data-id="element-164">
                    <span className="font-sora font-semibold text-white text-sm md:text-base" data-id="element-165">
                      I am a FoundHer
                    </span>
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-accent-pink group-hover:translate-x-1 transition-transform" data-id="element-166" />
                  </div>
                </button>
              </div>

              {/* Presented by */}
              <div className="pt-6 border-t border-white/10 flex flex-col items-center md:items-start text-center md:text-left space-y-1" data-id="element-167">
                <p className="font-inter text-white/50 text-[10px] md:text-xs uppercase tracking-widest" data-id="element-168">
                  Presented by
                </p>
                <p className="font-sora font-semibold text-white/90 text-sm md:text-base" data-id="element-169">
                  Protexxa &amp; FoundHers
                </p>
                <p className="font-inter text-white/40 text-[10px] md:text-xs mt-1" data-id="element-170">
                  Supported by the Ministry of Women and Gender Equality
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </main>;
}